<template>
  <IntercomLayout>
    <template #topbar>
      <SevaTopbar
        :business-name="businessName"
        :user="user"
        :global-user="globalUser"
        @settings="$router.push('/settings')"
        @logout="handleLogout"
      />
    </template>

    <template #sidebar>
      <IntercomSidebar :nav-sections="navSections" />
    </template>

    <div class="settings-view">
      <!-- Page Header -->
      <div class="settings-header">
        <h1 class="settings-title">{{ $t('settings.title') }}</h1>
        <p class="settings-subtitle">{{ $t('settings.subtitle') }}</p>
        <span class="account-id">Account ID: {{ businessId }}</span>
      </div>

      <!-- Settings Sections -->
      <div class="settings-sections">
        <!-- Trust Information -->
        <IntercomCard class="settings-card">
          <h3 class="section-title">{{ $t('settings.trustInfo') }}</h3>
          <p class="section-description">{{ $t('settings.trustInfoDesc') }}</p>
          <div class="form-grid">
            <IntercomInput
              v-model="trustSettings.trust_name"
              :label="$t('settings.trustName')"
              :placeholder="$t('settings.trustNamePlaceholder')"
              @blur="saveTrustSettings"
            />
            <IntercomPhoneInput
              v-model="trustSettings.trust_phone"
              :label="$t('settings.phone')"
              @update:valid="(v) => (phoneValid = v)"
              @blur="saveTrustSettings"
            />
            <IntercomEmailInput
              v-model="trustSettings.trust_email"
              :label="$t('settings.email')"
              :placeholder="$t('settings.emailPlaceholder')"
              class="span-2"
              @blur="saveTrustSettings"
            />
            <IntercomInput
              v-model="trustSettings.trust_address"
              :label="$t('settings.address')"
              :placeholder="$t('settings.addressPlaceholder')"
              class="span-2"
              @blur="saveTrustSettings"
            />
            <IntercomInput
              v-model="trustSettings.trust_80g_registration"
              :label="$t('settings.80gRegistration')"
              :placeholder="$t('settings.80gRegistrationPlaceholder')"
              @blur="saveTrustSettings"
            />
            <IntercomInput
              v-model="trustSettings.receipt_prefix"
              :label="$t('settings.receiptPrefix')"
              :placeholder="$t('settings.receiptPrefixPlaceholder')"
              @blur="saveTrustSettings"
            />
          </div>
        </IntercomCard>

        <!-- Registration & Compliance -->
        <IntercomCard class="settings-card">
          <h3 class="section-title">Registration & Compliance</h3>
          <p class="section-description">Tax registration details for 80G receipts and compliance filings</p>
          <div class="form-grid">
            <IntercomInput
              v-model="trustSettings.trust_pan"
              label="Trust PAN"
              placeholder="e.g., ABNTS8577J"
              @blur="saveTrustSettings"
            />
            <IntercomInput
              v-model="trustSettings.trust_80g_din"
              label="80G DIN (Order Number)"
              placeholder="e.g., ABNTS8577JF2026101"
              @blur="saveTrustSettings"
            />
            <IntercomDateInput
              v-model="trustSettings.trust_80g_valid_from"
              label="80G Valid From"
              @update:model-value="saveTrustSettings"
            />
            <IntercomDateInput
              v-model="trustSettings.trust_80g_valid_to"
              label="80G Valid To"
              @update:model-value="saveTrustSettings"
            />
            <IntercomInput
              v-model="trustSettings.trust_12a_number"
              label="12A Registration Number"
              placeholder="12A registration number"
              @blur="saveTrustSettings"
            />
            <IntercomInput
              v-model="trustSettings.trust_regd_no"
              label="Trust Deed Regd. No."
              placeholder="e.g., 14/2026 - Joint I SRO"
              @blur="saveTrustSettings"
            />
            <IntercomInput
              v-model="trustSettings.ngo_darpan_id"
              label="NGO Darpan ID"
              placeholder="e.g., TN/2026/0994583"
              @blur="saveTrustSettings"
            />
          </div>
        </IntercomCard>

        <!-- WhatsApp Automation -->
        <IntercomCard class="settings-card">
          <h3 class="section-title">{{ $t('settings.whatsappAutomation') }}</h3>
          <p class="section-description">{{ $t('settings.whatsappAutomationDesc') }}</p>
          <div class="toggle-list">
            <div class="toggle-row">
              <div class="toggle-info">
                <span class="toggle-label">{{ $t('settings.autoSendReminders') }}</span>
                <span class="toggle-description">{{ $t('settings.autoSendRemindersDesc') }}</span>
              </div>
              <IntercomToggle
                v-model="trustSettings.auto_send_reminders"
                @update:model-value="saveTrustSettings"
              />
            </div>
            <div class="toggle-row">
              <div class="toggle-info">
                <span class="toggle-label">{{ $t('settings.autoSendDonationThanks') }}</span>
                <span class="toggle-description">{{ $t('settings.autoSendDonationThanksDesc') }}</span>
              </div>
              <IntercomToggle
                v-model="trustSettings.auto_send_donation_thanks"
                @update:model-value="saveTrustSettings"
              />
            </div>
            <div class="toggle-row">
              <div class="toggle-info">
                <span class="toggle-label">{{ $t('settings.autoSend80gReceipt') }}</span>
                <span class="toggle-description">{{ $t('settings.autoSend80gReceiptDesc') }}</span>
              </div>
              <IntercomToggle
                v-model="trustSettings.auto_send_80g_receipt"
                @update:model-value="saveTrustSettings"
              />
            </div>
          </div>
        </IntercomCard>

        <!-- Display Settings -->
        <IntercomCard class="settings-card">
          <h3 class="section-title">{{ $t('settings.display') }}</h3>
          <div class="setting-row">
            <div class="setting-info">
              <span class="setting-label">{{ $t('settings.language') }}</span>
              <span class="setting-description">{{ $t('settings.languageDesc') }}</span>
            </div>
            <IntercomSelect
              v-model="displaySettings.language"
              :options="languageOptions"
              class="setting-select"
              @update:model-value="handleLanguageChange"
            />
          </div>
          <div class="setting-row">
            <div class="setting-info">
              <span class="setting-label">{{ $t('settings.currency') }}</span>
              <span class="setting-description">{{ $t('settings.currencyDesc') }}</span>
            </div>
            <IntercomSelect
              v-model="displaySettings.currency"
              :options="currencyOptions"
              class="setting-select"
              disabled
            />
          </div>
        </IntercomCard>

        <!-- Expense Categories -->
        <IntercomCard class="settings-card">
          <div class="section-header">
            <h3 class="section-title">{{ $t('expense.categories') }}</h3>
            <IntercomButton variant="secondary" size="sm" @click="showCategoryModal = true">
              {{ $t('expense.manageCategories') }}
            </IntercomButton>
          </div>
          <div v-if="categories.length === 0" class="empty-state">
            {{ $t('expense.noCategories') }}
          </div>
          <div v-else class="category-list">
            <IntercomBadge v-for="cat in categories" :key="cat.id" variant="info" class="category-badge">
              {{ cat.name }}
            </IntercomBadge>
          </div>
        </IntercomCard>

        <!-- About -->
        <IntercomCard class="settings-card">
          <h3 class="section-title">{{ $t('settings.about') }}</h3>
          <div class="about-info">
            <div class="about-row">
              <span class="about-label">{{ $t('settings.version') }}</span>
              <span class="about-value">1.0.0</span>
            </div>
            <div class="about-row">
              <span class="about-label">{{ $t('settings.module') }}</span>
              <span class="about-value">Seva - Trust Management</span>
            </div>
          </div>
        </IntercomCard>
      </div>

      <!-- Category Manager Modal -->
      <IntercomModal v-model:show="showCategoryModal" :title="$t('expense.manageCategories')">
        <CategoryManager
          :categories="categories"
          @create="handleCreateCategory"
          @update="handleUpdateCategory"
          @close="showCategoryModal = false"
        />
      </IntercomModal>
    </div>
  </IntercomLayout>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useToast } from '@shared/composables/useToast.js'
import { useAuth } from '@shared/composables/useAuth.js'
import { useBusiness } from '@shared/composables/useBusiness.js'
import { useSeva } from '../composables/useSeva.js'
import {
  IntercomLayout,
  IntercomSidebar,
  IntercomButton,
  IntercomCard,
  IntercomSelect,
  IntercomBadge,
  IntercomModal,
  IntercomInput,
  IntercomDateInput,
  IntercomPhoneInput,
  IntercomEmailInput,
  IntercomToggle,
} from '@shared/components/intercom'
import SevaTopbar from '../components/SevaTopbar.vue'
import CategoryManager from '../components/CategoryManager.vue'
import { useNavigation } from '../composables/useNavigation.js'

const { t, locale } = useI18n()
const router = useRouter()
const toast = useToast()
const { user, globalUser, logout } = useAuth()
const { businessName, businessId, fetchBusiness } = useBusiness()
const { listExpenseCategories, createExpenseCategory, updateExpenseCategory, getKVSettings, updateKVSettings } = useSeva()
const { navSections } = useNavigation()

// State
const categories = ref([])
const showCategoryModal = ref(false)
const isSaving = ref(false)
const phoneValid = ref(true)

const displaySettings = ref({
  language: 'en_IN',
  currency: 'INR',
})

// Trust settings from backend
const trustSettings = ref({
  trust_name: '',
  trust_address: '',
  trust_phone: '',
  trust_email: '',
  trust_80g_registration: '',
  receipt_prefix: 'DON',
  auto_send_reminders: true,
  auto_send_donation_thanks: true,
  auto_send_80g_receipt: false,
  trust_pan: '',
  trust_80g_din: '',
  trust_80g_valid_from: '',
  trust_80g_valid_to: '',
  trust_12a_number: '',
  trust_regd_no: '',
  ngo_darpan_id: '',
})

const languageOptions = computed(() => [
  { value: 'en_IN', label: 'English (India)' },
  { value: 'ta_IN', label: 'Tamil (தமிழ்)' },
])

const currencyOptions = computed(() => [
  { value: 'INR', label: '₹ Indian Rupee (INR)' },
])

// Helper to parse KV JSONB values (apiClient returns already-parsed JSON)
const parseKV = (val, defaultVal) => {
  if (val === undefined || val === null) return defaultVal
  return val
}

const fetchTrustSettings = async () => {
  try {
    const response = await getKVSettings()
    // KV API returns a flat key-value object
    const kv = response || {}
    trustSettings.value = {
      trust_name: parseKV(kv.trust_name, ''),
      trust_address: parseKV(kv.trust_address, ''),
      trust_phone: parseKV(kv.trust_phone, ''),
      trust_email: parseKV(kv.trust_email, ''),
      trust_80g_registration: parseKV(kv.trust_80g_registration, ''),
      receipt_prefix: parseKV(kv.receipt_prefix, 'DON'),
      auto_send_reminders: parseKV(kv.auto_send_reminders, true),
      auto_send_donation_thanks: parseKV(kv.auto_send_donation_thanks, true),
      auto_send_80g_receipt: parseKV(kv.auto_send_80g_receipt, false),
      trust_pan: parseKV(kv.trust_pan, ''),
      trust_80g_din: parseKV(kv.trust_80g_din, ''),
      trust_80g_valid_from: parseKV(kv.trust_80g_valid_from, ''),
      trust_80g_valid_to: parseKV(kv.trust_80g_valid_to, ''),
      trust_12a_number: parseKV(kv.trust_12a_number, ''),
      trust_regd_no: parseKV(kv.trust_regd_no, ''),
      ngo_darpan_id: parseKV(kv.ngo_darpan_id, ''),
    }
  } catch (error) {
    console.error('Failed to fetch settings:', error)
  }
}

const saveTrustSettings = async () => {
  if (isSaving.value) return
  if (trustSettings.value.trust_phone && !phoneValid.value) {
    toast.error(t('errors.invalidPhone'))
    return
  }
  isSaving.value = true
  try {
    await updateKVSettings(trustSettings.value)
    toast.success(t('settings.saveSuccess'))
  } catch (error) {
    console.error('Failed to save settings:', error)
    toast.error(error.detail || error.message || t('settings.saveFailed'))
  } finally {
    isSaving.value = false
  }
}

const fetchCategories = async () => {
  try {
    const response = await listExpenseCategories()
    // apiClient.get() unwraps response.data.data, so we get { categories: [...] } directly
    categories.value = response?.categories || []
  } catch (error) {
    console.error('Failed to fetch categories:', error)
  }
}

const handleLanguageChange = (newLocale) => {
  locale.value = newLocale
  localStorage.setItem('seva_locale', newLocale)
  toast.success(t('settings.languageChanged'))
}

const handleCreateCategory = async (data) => {
  try {
    await createExpenseCategory(data)
    toast.success(t('success.categoryCreated'))
    fetchCategories()
  } catch (error) {
    console.error('Failed to create category:', error)
    toast.error(error.detail || error.message || t('errors.saveFailed'))
  }
}

const handleUpdateCategory = async (id, data) => {
  try {
    await updateExpenseCategory(id, data)
    toast.success(t('success.categoryUpdated'))
    fetchCategories()
  } catch (error) {
    console.error('Failed to update category:', error)
    toast.error(error.detail || error.message || t('errors.saveFailed'))
  }
}

const handleLogout = async () => {
  await logout()
  router.push('/login')
}

onMounted(() => {
  fetchBusiness()
  fetchCategories()
  fetchTrustSettings()
  // Load saved locale
  const savedLocale = localStorage.getItem('seva_locale')
  if (savedLocale) {
    displaySettings.value.language = savedLocale
    locale.value = savedLocale
  }
})
</script>

<style scoped>
@import '@shared/styles/intercom-tokens.css';

.settings-view {
  padding: var(--space-8);
  max-width: var(--content-max-width);
  margin: 0 auto;
}

.settings-header {
  margin-bottom: var(--space-6);
}

.settings-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
  margin: 0 0 var(--space-1) 0;
}

.settings-subtitle {
  font-size: var(--font-size-base);
  color: var(--color-gray-500);
  margin: 0;
}

.account-id {
  display: inline-block;
  font-size: 12px;
  color: var(--text-tertiary, #98a2b3);
  background: var(--bg-secondary, #f9fafb);
  padding: 2px 8px;
  border-radius: 4px;
  font-family: monospace;
  margin-top: 4px;
}

.settings-sections {
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
}

.settings-card {
  padding: var(--space-5);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--space-4);
}

.section-title {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--color-charcoal);
  margin: 0 0 var(--space-4) 0;
}

.section-header .section-title {
  margin-bottom: 0;
}

.setting-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--space-3) 0;
  border-bottom: 1px solid var(--color-gray-100);
}

.setting-row:last-child {
  border-bottom: none;
}

.setting-info {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.setting-label {
  font-weight: var(--font-weight-medium);
  color: var(--color-charcoal);
}

.setting-description {
  font-size: var(--font-size-sm);
  color: var(--color-gray-500);
}

.setting-select {
  width: 200px;
}

.category-list {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
}

.category-badge {
  font-size: var(--font-size-sm);
}

.empty-state {
  text-align: center;
  color: var(--color-gray-500);
  padding: var(--space-4);
}

.about-info {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.about-row {
  display: flex;
  justify-content: space-between;
}

.about-label {
  color: var(--color-gray-500);
}

.about-value {
  color: var(--color-charcoal);
  font-weight: var(--font-weight-medium);
}

.section-description {
  font-size: var(--font-size-sm);
  color: var(--color-gray-500);
  margin: 0 0 var(--space-4) 0;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--space-4);
}

.form-grid .span-2 {
  grid-column: span 2;
}

.toggle-list {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.toggle-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--space-3) 0;
  border-bottom: 1px solid var(--color-gray-100);
}

.toggle-row:last-child {
  border-bottom: none;
}

.toggle-info {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  flex: 1;
  margin-right: var(--space-4);
}

.toggle-label {
  font-weight: var(--font-weight-medium);
  color: var(--color-charcoal);
}

.toggle-description {
  font-size: var(--font-size-sm);
  color: var(--color-gray-500);
}

@media (max-width: 768px) {
  .settings-view {
    padding: var(--space-4);
  }

  .setting-row {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--space-3);
  }

  .setting-select {
    width: 100%;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }

  .form-grid .span-2 {
    grid-column: span 1;
  }
}
</style>
